import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { db } from "../lib/firebase";
import {
  addDoc, collection, getDocs, query, serverTimestamp, where, doc, updateDoc, increment,
} from "firebase/firestore";
import {
  TIME_SLOTS, WINDOWS, windowForSlot,
  buildSlotAvailability,
} from "../lib/constants";
import { toast } from "sonner";
import { Plus, UserPlus, Trash2 } from "lucide-react";
import { CarsList } from "../pages/Profile";
import { Spinner } from "./Spinner";
import { useCatalog } from "../lib/CatalogContext";

/* =================== ADD BOOKING DIALOG =================== */
export function AdminAddBookingDialog({ users, cars = [], products = [], onCreated }) {
  const { vehicleCategories, individualRates } = useCatalog();
  const [open, setOpen] = useState(false);
  const [localUsers, setLocalUsers] = useState(users);
  const [uid, setUid] = useState("");
  const [vehicleId, setVehicleId] = useState("hatchback_compact");
  const [serviceId, setServiceId] = useState("basic_hatchback");
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [customServices, setCustomServices] = useState([]);
  const [bikeWaxing, setBikeWaxing] = useState(false);
  const [priceOverride, setPriceOverride] = useState("");
  const [priceNote, setPriceNote] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => { setLocalUsers(users); }, [users]);

  const customers = useMemo(
    () => localUsers.filter((u) => u.role !== "admin"),
    [localUsers]
  );
  const carsByUid = useMemo(() => {
    const map = {};
    cars.forEach((c) => {
      if (!c.uid) return;
      if (!map[c.uid]) map[c.uid] = [];
      map[c.uid].push(c);
    });
    return map;
  }, [cars]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((u) => {
      const userCars = carsByUid[u.id] || [];
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        userCars.some((c) => (c.registration || "").toLowerCase().includes(q))
      );
    });
  }, [customers, carsByUid, search]);

  const vehicleTypes = useMemo(() => vehicleCategories.map((c) => ({
    id: c.id, label: c.short, priceKey: c.priceKey, icon: "Car",
  })), [vehicleCategories]);
  const services = useMemo(() => individualRates
    .filter((r) => r.id !== "bike_wash")
    .map((r) => ({
      id: r.id, label: r.label, basePrice: r.interiorExterior, duration: "45 min",
      desc: `Single wash · ₹${r.exteriorOnly} ext-only / ₹${r.interiorExterior} int+ext`,
    })), [individualRates]);

  const vehicle = vehicleTypes.find((v) => v.id === vehicleId);
  const isBike = vehicleId === "bike";
  const vehicleSuffix = vehicle?.priceKey === "priceSUV" ? "suv" : "hatchback";
  const filteredServices = services.filter((s) => s.id.endsWith(vehicleSuffix));
  const service = filteredServices.find((s) => s.id === serviceId) || filteredServices[0];
  const bikeRate = individualRates.find((r) => r.id === "bike_wash");
  const bikePrice = (bikeRate?.washPolish || 0) + (bikeWaxing ? (bikeRate?.waxing || 0) : 0);
  const price = isBike ? bikePrice : (service?.basePrice || 0);
  const customServicesTotal = customServices.reduce((sum, s) => sum + (Number(s.fee) || 0), 0);
  const productsTotal = selectedProducts.reduce((sum, s) => {
    const p = products.find((pr) => pr.id === s.productId);
    return sum + (p?.price || 0) * (Number(s.qty) || 0);
  }, 0);
  const suggestedTotal = price + customServicesTotal + productsTotal;
  const hasOverride = priceOverride.trim() !== "";
  const totalPrice = hasOverride ? Number(priceOverride) || 0 : suggestedTotal;
  const customer = localUsers.find((u) => u.id === uid);

  const addCustomService = () => {
    setCustomServices((prev) => [...prev, { name: "", fee: "" }]);
  };
  const updateCustomService = (idx, field, value) => {
    setCustomServices((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const removeCustomService = (idx) => {
    setCustomServices((prev) => prev.filter((_, i) => i !== idx));
  };

  const addProductLine = () => {
    setSelectedProducts((prev) => [...prev, { productId: products[0]?.id || "", qty: "1" }]);
  };
  const updateProductLine = (idx, field, value) => {
    setSelectedProducts((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const removeProductLine = (idx) => {
    setSelectedProducts((prev) => prev.filter((_, i) => i !== idx));
  };
  const productLineError = (line) => {
    const p = products.find((pr) => pr.id === line.productId);
    if (!p) return "Pick a product";
    const qty = Number(line.qty);
    if (!Number.isFinite(qty) || qty <= 0) return "Enter a valid quantity";
    if (qty > (p.quantity || 0)) return `Only ${p.quantity} in stock`;
    return null;
  };

  useEffect(() => {
    if (!date) return;
    const dateStr = date.toISOString().split("T")[0];
    setSlotsLoading(true);
    (async () => {
      try {
        const q = query(
          collection(db, "bookings"),
          where("date", "==", dateStr),
          where("status", "in", ["pending", "confirmed"])
        );
        const snap = await getDocs(q);
        setBookedSlots(snap.docs.map((d) => d.data().timeSlot));
      } finally {
        setSlotsLoading(false);
      }
    })();
  }, [date]);

  const { map } = useMemo(() => buildSlotAvailability(bookedSlots, date), [bookedSlots, date]);

  const reset = () => {
    setUid(""); setVehicleId("hatchback_compact"); setServiceId("basic_hatchback");
    setDate(undefined); setTime(""); setBookedSlots([]); setSearch(""); setCustomServices([]);
    setBikeWaxing(false); setPriceOverride(""); setPriceNote(""); setSelectedProducts([]);
  };

  const handleNewCustomer = (newUser) => {
    setLocalUsers((prev) => [...prev, newUser]);
    setUid(newUser.id);
    setSearch("");
    onCreated?.();
  };

  const handleCreate = async () => {
    if (!uid || !date || !time) {
      toast.error("Pick customer, date and time");
      return;
    }
    if (!vehicle || (!isBike && !service)) {
      toast.error("Vehicle/service catalog isn't set up yet — check Firestore vehicleTypes/individual_rates");
      return;
    }
    if (hasOverride && (!Number.isFinite(Number(priceOverride)) || Number(priceOverride) < 0)) {
      toast.error("Enter a valid final price");
      return;
    }
    const productLineErrors = selectedProducts.map(productLineError).filter(Boolean);
    if (productLineErrors.length > 0) {
      toast.error(productLineErrors[0]);
      return;
    }
    const cleanCustomServices = customServices
      .filter((s) => s.name.trim())
      .map((s) => ({ name: s.name.trim(), fee: Number(s.fee) || 0 }));
    const cleanProducts = selectedProducts.map((s) => {
      const p = products.find((pr) => pr.id === s.productId);
      return { productId: p.id, name: p.name, price: p.price, qty: Number(s.qty) };
    });
    const finalPrice = hasOverride ? Number(priceOverride) : suggestedTotal;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "bookings"), {
        uid: customer.id,
        userPhone: customer.phone || "",
        userName: customer.name || customer.phone || "Customer",
        vehicleType: vehicle.id,
        vehicleLabel: vehicle.label,
        serviceType: isBike ? "bike_wash" : service.id,
        serviceLabel: isBike ? `${bikeRate?.label || "Wash & Polish"}${bikeWaxing ? " + Waxing" : ""}` : service.label,
        bikeWaxing: isBike ? bikeWaxing : null,
        basePrice: price,
        customServices: cleanCustomServices,
        products: cleanProducts,
        price: finalPrice,
        suggestedPrice: hasOverride && finalPrice !== suggestedTotal ? suggestedTotal : null,
        priceNote: priceNote.trim() || null,
        date: date.toISOString().split("T")[0],
        timeSlot: time,
        status: "confirmed",
        paymentMode: "cash",
        createdBy: "admin",
        createdAt: serverTimestamp(),
      });
      // Deduct sold quantity from inventory. Best-effort per product so one
      // failure doesn't undo the booking that already succeeded above.
      for (const p of cleanProducts) {
        try {
          await updateDoc(doc(db, "products", p.productId), { quantity: increment(-p.qty) });
        } catch (e) {
          console.warn(`Failed to deduct stock for ${p.name}:`, e.message);
        }
      }
      toast.success(`Booked ${time} on ${date.toISOString().split("T")[0]} for ${customer.name}`);
      reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button
          data-testid="admin-add-booking-button"
          className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider text-xs h-12 px-6"
        >
          <Plus size={16} className="mr-2" /> Add Booking
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl rounded-none border-[#D4D4D8] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tighter">New Booking (for customer)</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* CUSTOMER PICKER */}
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <label className="font-mono-accent text-xs text-[#525252]">CUSTOMER ({customers.length})</label>
              <AdminAddUserDialog
                triggerLabel="+ NEW CUSTOMER"
                triggerVariant="inline"
                onCreated={handleNewCustomer}
              />
            </div>
            <Input
              data-testid="add-booking-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, or vehicle number…"
              className="rounded-none border-[#D4D4D8] mb-2 h-10"
            />
            <div className="border border-[#D4D4D8] max-h-44 overflow-y-auto">
              {customers.length === 0 && (
                <div className="p-6 text-center bg-[#F4F4F5]">
                  <div className="font-mono-accent text-xs text-[#525252] mb-3">NO CUSTOMERS YET</div>
                  <div className="text-sm text-[#525252] mb-4">
                    Customers appear here when they log in via OTP, or when you add them manually.
                  </div>
                  <AdminAddUserDialog
                    triggerLabel="+ ADD FIRST CUSTOMER"
                    triggerVariant="inline-button"
                    onCreated={handleNewCustomer}
                  />
                </div>
              )}
              {customers.length > 0 && filtered.length === 0 && (
                <div className="p-4 text-xs text-[#525252] text-center">
                  No customer matches "{search}". Click <span className="font-bold">+ NEW CUSTOMER</span> above to add one.
                </div>
              )}
              {filtered.map((u) => {
                const active = uid === u.id;
                return (
                  <button
                    key={u.id}
                    data-testid={`add-booking-customer-${u.id}`}
                    onClick={() => setUid(u.id)}
                    className={`w-full text-left px-4 py-2 border-b border-[#D4D4D8] last:border-b-0 text-sm flex justify-between items-center transition-colors ${active ? "bg-[#002FA7] text-white" : "hover:bg-[#F4F4F5]"}`}
                  >
                    <span className="font-bold">{u.name || "—"}</span>
                    <span className={`font-mono text-xs ${active ? "text-white/80" : "text-[#525252]"}`}>{u.phone || "no phone"}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CUSTOMER'S CARS */}
          {customer && (
            <div className="border border-[#D4D4D8] p-4 bg-[#F4F4F5]">
              <CarsList uid={customer.id} allowAdd={true} allowDelete={false} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">VEHICLE</label>
              <Select value={vehicleId} onValueChange={(v) => { setVehicleId(v); setServiceId(""); setBikeWaxing(false); }}>
                <SelectTrigger data-testid="add-booking-vehicle" className="rounded-none border-[#D4D4D8]"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {vehicleTypes.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isBike && (
              <div>
                <label className="font-mono-accent text-xs text-[#525252] block mb-2">SERVICE</label>
                <Select value={service?.id || ""} onValueChange={setServiceId}>
                  <SelectTrigger data-testid="add-booking-service" className="rounded-none border-[#D4D4D8]"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {filteredServices.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.label} · ₹{s.basePrice}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isBike && (
              <div>
                <label className="font-mono-accent text-xs text-[#525252] block mb-2">SERVICE</label>
                <button
                  type="button"
                  data-testid="add-booking-bike-waxing-toggle"
                  onClick={() => setBikeWaxing((v) => !v)}
                  className={`w-full h-10 px-3 flex items-center justify-between border text-xs transition-colors ${
                    bikeWaxing ? "border-[#002FA7] bg-[#F4F4F5]" : "border-[#D4D4D8] hover:bg-[#F4F4F5]"
                  }`}
                >
                  <span>{bikeRate?.label || "Wash & Polish"} · ₹{bikeRate?.washPolish ?? 0}{bikeWaxing ? ` + Waxing ₹${bikeRate?.waxing ?? 0}` : ""}</span>
                  <span className={`w-4 h-4 border shrink-0 flex items-center justify-center ${bikeWaxing ? "bg-[#002FA7] border-[#002FA7] text-white" : "border-[#D4D4D8]"}`}>
                    {bikeWaxing ? "✓" : "+"}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* CUSTOM ADD-ON SERVICES */}
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <label className="font-mono-accent text-xs text-[#525252]">ADD-ON SERVICES (OPTIONAL)</label>
              <button
                type="button"
                data-testid="add-booking-add-custom-service"
                onClick={addCustomService}
                className="font-mono-accent text-[10px] text-[#002FA7] hover:underline flex items-center gap-1"
              >
                <Plus size={12} /> ADD SERVICE
              </button>
            </div>
            {customServices.length > 0 && (
              <div className="space-y-2">
                {customServices.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      data-testid={`add-booking-custom-service-name-${idx}`}
                      value={s.name}
                      onChange={(e) => updateCustomService(idx, "name", e.target.value)}
                      placeholder="Service name, e.g. Engine bay cleaning"
                      className="rounded-none border-[#D4D4D8] h-10 flex-1"
                    />
                    <Input
                      data-testid={`add-booking-custom-service-fee-${idx}`}
                      type="number"
                      value={s.fee}
                      onChange={(e) => updateCustomService(idx, "fee", e.target.value)}
                      placeholder="₹ fee"
                      className="rounded-none border-[#D4D4D8] h-10 w-28"
                    />
                    <button
                      type="button"
                      data-testid={`add-booking-remove-custom-service-${idx}`}
                      onClick={() => removeCustomService(idx)}
                      className="text-[#FF2A2A] hover:opacity-70 shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* PRODUCTS SOLD */}
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <label className="font-mono-accent text-xs text-[#525252]">PRODUCTS (OPTIONAL)</label>
              <button
                type="button"
                data-testid="add-booking-add-product"
                onClick={addProductLine}
                disabled={products.length === 0}
                className="font-mono-accent text-[10px] text-[#002FA7] hover:underline flex items-center gap-1 disabled:opacity-40 disabled:no-underline"
              >
                <Plus size={12} /> ADD PRODUCT
              </button>
            </div>
            {products.length === 0 && (
              <div className="text-xs text-[#525252]">No products in inventory yet — add some in the Products tab.</div>
            )}
            {selectedProducts.length > 0 && (
              <div className="space-y-2">
                {selectedProducts.map((s, idx) => {
                  const p = products.find((pr) => pr.id === s.productId);
                  const err = productLineError(s);
                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-2">
                        <Select value={s.productId} onValueChange={(v) => updateProductLine(idx, "productId", v)}>
                          <SelectTrigger data-testid={`add-booking-product-select-${idx}`} className="rounded-none border-[#D4D4D8] h-10 flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {products.map((pr) => (
                              <SelectItem key={pr.id} value={pr.id}>{pr.name} · ₹{pr.price} · {pr.quantity} in stock</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          data-testid={`add-booking-product-qty-${idx}`}
                          type="number"
                          value={s.qty}
                          onChange={(e) => updateProductLine(idx, "qty", e.target.value)}
                          placeholder="QTY"
                          className="rounded-none border-[#D4D4D8] h-10 w-20"
                        />
                        <div className="w-20 text-right text-sm font-bold">
                          ₹{((p?.price || 0) * (Number(s.qty) || 0)).toLocaleString("en-IN")}
                        </div>
                        <button
                          type="button"
                          data-testid={`add-booking-remove-product-${idx}`}
                          onClick={() => removeProductLine(idx)}
                          className="text-[#FF2A2A] hover:opacity-70 shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {err && <div className="text-[10px] text-[#FF2A2A] mt-1">{err}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* FINAL PRICE OVERRIDE — for walk-in discounts */}
          <div>
            <label className="font-mono-accent text-xs text-[#525252] block mb-2">
              FINAL PRICE CHARGED (OPTIONAL — OVERRIDES SUGGESTED ₹{suggestedTotal})
            </label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="add-booking-price-override"
                type="number"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
                placeholder={`Suggested ₹${suggestedTotal}`}
                className="rounded-none border-[#D4D4D8] h-10 w-36"
              />
              <Input
                data-testid="add-booking-price-note"
                value={priceNote}
                onChange={(e) => setPriceNote(e.target.value)}
                placeholder="Discount reason, e.g. loyalty discount (optional)"
                className="rounded-none border-[#D4D4D8] h-10 flex-1"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-[#D4D4D8] p-2">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { setDate(d); setTime(""); }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-none"
              />
            </div>
            <div className="max-h-[280px] overflow-y-auto space-y-4">
              {!date && <div className="text-sm text-[#525252]">Pick a date.</div>}
              {date && slotsLoading && (
                <div className="flex items-center gap-2 text-sm text-[#525252]">
                  <Spinner /> Checking availability…
                </div>
              )}
              {date && !slotsLoading && WINDOWS.map((w) => {
                const wSlots = TIME_SLOTS
                  .filter((s) => windowForSlot(s) === w.key)
                  .filter((s) => !map[s].past);
                if (wSlots.length === 0) return null;
                return (
                  <div key={w.key}>
                    <div className="flex justify-between mb-1">
                      <span className="font-mono-accent text-[10px] text-[#525252]">{w.label}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-px bg-[#D4D4D8] border border-[#D4D4D8]">
                      {wSlots.map((t) => {
                        const av = map[t];
                        return (
                          <button
                            key={t}
                            disabled={!av.ok}
                            onClick={() => setTime(t)}
                            className={`py-2 font-mono text-xs ${
                              time === t
                                ? "bg-[#002FA7] text-white"
                                : !av.ok
                                ? "bg-white text-[#D4D4D8] cursor-not-allowed line-through"
                                : "bg-white text-[#0A0A0A] hover:bg-[#F4F4F5]"
                            }`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-[#D4D4D8]">
            <div>
              <div className="font-mono-accent text-[10px] text-[#525252]">
                {customer ? `FOR ${customer.name?.toUpperCase()}` : "SELECT CUSTOMER"}
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-display font-black text-2xl text-[#002FA7]">₹{totalPrice}</div>
                {hasOverride && totalPrice !== suggestedTotal && (
                  <div className="font-mono text-xs text-[#525252] line-through">₹{suggestedTotal}</div>
                )}
              </div>
              {(customServicesTotal > 0 || productsTotal > 0) && (
                <div className="font-mono text-[10px] text-[#525252]">
                  ₹{price} base
                  {customServicesTotal > 0 ? ` + ₹${customServicesTotal} add-ons` : ""}
                  {productsTotal > 0 ? ` + ₹${productsTotal} products` : ""}
                </div>
              )}
            </div>
            <Button
              data-testid="add-booking-submit"
              onClick={handleCreate}
              disabled={submitting || !uid || !date || !time || selectedProducts.some((s) => !!productLineError(s))}
              className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12 px-6 disabled:opacity-40"
            >
              {submitting ? <Spinner /> : "Confirm Booking"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =================== ADD USER DIALOG =================== */
export function AdminAddUserDialog({ onCreated, triggerLabel, triggerVariant }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", role: "customer" });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const phoneInput = form.phone.trim();
      const phone = phoneInput.startsWith("+")
        ? phoneInput
        : `+91${phoneInput.replace(/\D/g, "").slice(-10)}`;

      const dupSnap = await getDocs(query(collection(db, "users"), where("phone", "==", phone)));
      const existing = dupSnap.docs.find((d) => !d.data().merged);
      if (existing) {
        toast.error("A customer with this phone number already exists");
        setSaving(false);
        return;
      }

      const ref = await addDoc(collection(db, "users"), {
        name: form.name.trim(),
        phone,
        role: form.role,
        manual: true,
        createdAt: serverTimestamp(),
      });
      const newUser = { id: ref.id, name: form.name.trim(), phone, role: form.role, manual: true };
      toast.success(`Added ${newUser.name}`);
      setForm({ name: "", phone: "", role: "customer" });
      setOpen(false);
      onCreated?.(newUser);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const trigger = triggerVariant === "inline" ? (
    <button
      data-testid="admin-add-user-inline-button"
      type="button"
      className="font-mono-accent text-xs text-[#002FA7] hover:underline"
    >
      {triggerLabel || "+ ADD CUSTOMER"}
    </button>
  ) : triggerVariant === "inline-button" ? (
    <Button
      data-testid="admin-add-user-cta-button"
      type="button"
      className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider text-xs h-10 px-5"
    >
      <UserPlus size={14} className="mr-2" /> {triggerLabel || "ADD CUSTOMER"}
    </Button>
  ) : (
    <Button
      data-testid="admin-add-user-button"
      variant="outline"
      className="border-2 border-black rounded-none hover:bg-black hover:text-white font-bold uppercase tracking-wider text-xs h-12 px-6"
    >
      <UserPlus size={16} className="mr-2" /> Add User
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md rounded-none border-[#D4D4D8]">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tighter">New Customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <label className="font-mono-accent text-xs text-[#525252] block mb-2">NAME</label>
            <Input
              data-testid="add-user-name"
              value={form.name}
              onChange={update("name")}
              className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
              placeholder="Rahul Verma"
            />
          </div>
          <div>
            <label className="font-mono-accent text-xs text-[#525252] block mb-2">PHONE</label>
            <Input
              data-testid="add-user-phone"
              value={form.phone}
              onChange={update("phone")}
              className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
              placeholder="9XXXXXXXXX or +91..."
            />
          </div>

          <div className="text-xs text-[#525252] font-mono-accent border border-[#FFC000] bg-[#FFFCE6] p-3">
            NOTE: Creates a customer record so admin can book on their behalf. The actual auth account is created when this person logs in with the same phone via OTP.
          </div>

          <Button
            data-testid="add-user-submit"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12"
          >
            {saving ? <Spinner /> : "Add Customer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
